import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes'
import { createApi, fetchBaseQuery, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'
import { apiPath, baseUrl } from 'config'

import {
  Application,
  ApplicationsResponse,
  ApplicationsTransformResponse,
} from 'store/reducers-admin/Applications/type'
import { getSignature } from 'utils/getSignature'

export const queryApplications = createApi({
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    approveApplication: builder.mutation<boolean, string>({
      invalidatesTags: ['GetApplications'],
      queryFn: async (appId, _api, _extraOptions, baseQuery) => {
        const { deadline, signature } = await getSignature({
          primaryType: 'ApproveFundApplication',
          url: `${apiPath.application}/${appId}/approve`,
        })

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url: `${apiPath.application}/${appId}/approve`,
        })
        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }
        return { data: result.data as boolean }
      },
    }),
    declineApplication: builder.mutation<boolean, string>({
      invalidatesTags: ['GetApplications'],
      queryFn: async (appId, _api, _extraOptions, baseQuery) => {
        const { deadline, signature } = await getSignature({
          primaryType: 'DeclineFundApplication',
          url: `${apiPath.application}/${appId}/decline`,
        })

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url: `${apiPath.application}/${appId}/decline`,
        })
        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }
        return { data: result.data as boolean }
      },
    }),
    getApplications: builder.query<ApplicationsTransformResponse, { limit?: number } | void>({
      providesTags: ['GetApplications'],
      queryFn: async (args, _api, _extraOptions, baseQuery) => {
        const limit = args ? `?limit=${args?.limit}` : '?limit=99' // temporary for mvp

        const url = `${apiPath.application}${limit}`

        const { deadline, signature } = await getSignature({
          primaryType: 'GetFundApplications',
          url,
        })

        const result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta> = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          url,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }

        const { data } = result.data as ApplicationsResponse

        const applications = {
          new: [] as Array<Application>,
          processed: [] as Array<Application>,
        }

        data.forEach((application) => {
          if (application.status === 'Pending') {
            applications.new.push(application)
          } else {
            applications.processed.push(application)
          }
        })
        return { data: applications as ApplicationsTransformResponse }
      },
    }),
  }),
  reducerPath: 'applications',
  tagTypes: ['GetApplications'],
})

export const {
  useApproveApplicationMutation,
  useDeclineApplicationMutation,
  useGetApplicationsQuery,
  useLazyGetApplicationsQuery,
} = queryApplications
